import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, Plus, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';
import styles from '../styles/Recordings.module.css';
import { useDocumentHead } from '../hooks/useDocumentHead';

const Recordings = () => {
  useDocumentHead(
    'My Presentations | TopSpeaks',
    'Access and manage your recorded speaking sessions. Review past performances, track progress, and get detailed AI-powered feedback to improve your speaking skills.',
    'index, follow'
  );
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPresentations = async () => {
      try {
        const data = await authenticatedFetch('/api/presentation/presentations');
        setPresentations(data);
      } catch (err) {
        if (err.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to load presentations. Please try again later.');
          console.error('Error fetching presentations:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPresentations();
  }, [navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSectionDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const groupPresentationsByDate = () => {
    const grouped = presentations.reduce((groups, presentation) => {
      const date = new Date(presentation.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(presentation);
      return groups;
    }, {});

    // Sort dates in descending order
    return Object.entries(grouped)
      .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
      .map(([date, items]) => ({
        date,
        title: formatSectionDate(date),
        items: items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      }));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <main className={styles.main}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
            Loading presentations...
          </div>
        </main>
      </div>
    );
  }

  const groupedPresentations = groupPresentationsByDate();

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <div className={styles.headerSection}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Presentation Recordings</h1>
            <p className={styles.subtitle}>
              Manage and review your recorded presentation sessions
            </p>
          </div>
          <Link to="/practice" className={styles.newRecordingButton}>
            <Plus size={18} />
            New Recording
          </Link>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!error && presentations.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateContent}>
              <h2 className={styles.emptyStateTitle}>No recordings yet</h2>
              <p className={styles.emptyStateText}>
                Start by recording your first presentation to receive feedback and track your progress.
              </p>
              <Link to="/practice" className={styles.startRecordingButton}>
                <Plus size={18} />
                Record First Presentation
              </Link>
            </div>
          </div>
        ) : (
          <div className={styles.presentationsContainer}>
            {groupedPresentations.map((group) => (
              <section key={group.date} className={styles.presentationSection}>
                <h2 className={styles.sectionTitle}>{group.title}</h2>
                <div className={styles.presentationsList}>
                  {group.items.map((presentation) => (
                    <Link
                      to={`/presentations/${presentation._id}`}
                      key={presentation._id}
                      className={styles.presentationCard}
                    >
                      <div className={styles.presentationContent}>
                        <div className={styles.presentationHeader}>
                          <h3 className={styles.presentationTitle}>
                            {presentation.title || 'Untitled Presentation'}
                          </h3>
                          <ChevronRight className={styles.chevron} size={18} />
                        </div>
                        <div className={styles.presentationMeta}>
                          <Clock size={14} />
                          <span>{formatDate(presentation.created_at)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Recordings;